import { useState, useCallback } from 'react';
import { Loader2, Plus, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils/cn';
import { useAddBlockedWords, useUploadBlockedWordsFile } from '../api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddBlockedWordsDialog({ open, onOpenChange }: Props) {
  /* ----- manual text state ----- */
  const [text, setText] = useState('');

  /* ----- file upload state ----- */
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const addBlockedWords = useAddBlockedWords();
  const uploadFile = useUploadBlockedWordsFile();

  const isBusy = addBlockedWords.isPending || uploadFile.isPending;

  /* --------------- file handling --------------- */

  const acceptFile = useCallback((f: File) => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
    if (ext !== '.csv' && ext !== '.txt') {
      toast.error('Only .csv or .txt files are supported.');
      return;
    }
    setFile(f);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) acceptFile(f);
    },
    [acceptFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) acceptFile(f);
      e.target.value = '';
    },
    [acceptFile],
  );

  /* --------------- submit --------------- */

  const handleSubmit = () => {
    const hasText = text.trim().length > 0;
    const hasFile = file !== null;

    if (!hasText && !hasFile) {
      toast.error('Enter words manually or upload a file.');
      return;
    }

    let pending = 0;
    let completed = 0;
    const finish = () => {
      completed++;
      if (completed >= pending) {
        setText('');
        setFile(null);
        onOpenChange(false);
      }
    };

    // ---- Submit manual words ----
    if (hasText) {
      const words = text
        .split(/[\n,]+/)
        .map((w) => w.trim().toLowerCase())
        .filter(Boolean);
      const unique = [...new Set(words)];
      if (unique.length === 0) {
        toast.error('Enter at least one valid word.');
        return;
      }
      pending++;
      addBlockedWords.mutate(unique, {
        onSuccess: (res) => {
          toast.success(
            res.added && res.added > 0
              ? `Added ${res.added} new word${res.added > 1 ? 's' : ''}.`
              : 'All words already exist in the list.',
          );
          finish();
        },
        onError: () => toast.error('Could not add words. Try again.'),
      });
    }

    // ---- Submit file ----
    if (hasFile) {
      pending++;
      uploadFile.mutate(file, {
        onSuccess: (res) => {
          toast.success(
            res.added > 0
              ? `File uploaded — ${res.added} new word${res.added > 1 ? 's' : ''} added.`
              : 'File processed — all words already exist.',
          );
          finish();
        },
        onError: () => toast.error('File upload failed. Try again.'),
      });
    }
  };

  /* --------------- unique word count from textarea --------------- */
  const manualCount =
    text.trim().length > 0
      ? new Set(
          text
            .split(/[\n,]+/)
            .map((w) => w.trim().toLowerCase())
            .filter(Boolean),
        ).size
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Blocked Words</DialogTitle>
          <DialogDescription>
            Type words manually, upload a file, or do both. Words are
            case-insensitive and duplicates are ignored.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ========== SECTION 1: Manual textarea ========== */}
          <div className="space-y-2">
            <Label htmlFor="blocked-words-input">Type Words</Label>
            <Textarea
              id="blocked-words-input"
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'e.g.\nword1\nword2, word3'}
              className="font-mono text-sm"
              disabled={isBusy}
            />
            {manualCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {manualCount} unique {manualCount === 1 ? 'word' : 'words'}{' '}
                detected
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">OR</span>
            <Separator className="flex-1" />
          </div>

          {/* ========== SECTION 2: File upload (drag & drop) ========== */}
          <div className="space-y-2">
            <Label>Upload File</Label>

            {!file ? (
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!isBusy) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input bg-background p-6 text-center transition-colors hover:border-ring',
                  dragOver && 'border-primary bg-accent',
                  isBusy && 'cursor-not-allowed opacity-50',
                )}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Upload className="h-5 w-5" />
                </div>
                <p className="text-sm font-medium">
                  Drop a .csv or .txt file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={handleFileInput}
                  disabled={isBusy}
                />
              </label>
            ) : (
              <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setFile(null)}
                  disabled={isBusy}
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isBusy}>
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Add Words
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}